import { css } from 'styled-components';
import { rgba } from 'polished';
import { createTheme } from './basicTheme';

const breakpoints = { xs: 0, sm: 480, md: 769, lg: 1024, xl: 1200, xxl: 1440 };

const zIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const space = [0, 5, 10, 15, 20];

const fonts = {
  base: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  roboto: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  robotoSlab: '"RobotoSlab", "Helvetica Neue", Helvetica, Arial, sans-serif',
  playfair: "'Playfair Display', Georgia, serif",
  dmSans: "'DM Sans', system-ui, sans-serif"
};

const fontSizes = [14, 16, 18, 20, 28, 32, 38, 50, 64, 72];

const colors = {
  secondary: '#00aeeb',
  secondaryHover: '#4eccf9',
  tertiary: '#203669',
  bgPrimary: '#f9f9f9',
  white: '#ffffff',
  text: '#4b4e55',
  marine: '#00376b',
  azure: '#00a0ff',
  topaz: '#19abb4',
  cerulean: '#0277bd',
  aquaBlue: '#07c6ea',
  dodgerBlue: '#50abf1',
  monotoneGrey: '#555555',
  iceBlue: '#ebeff2',
  iceBlueTwo: '#ebf0f5',
  turquoise: '#02b5bd',
  greenBlue: '#22c993',
  placeholder: '#4f5568',
  hoverButton: '#2ce0a6',
  linkPrimary: '#00a0ff',
  linkPrimaryVisited: '#b40fb4',
  backgroundLight: '#f9fafb',
  weirdGreen: '#4acc8d',
  weirdGreenDarken: '#32b173',
  lightGrey: '#aab7c5',
  lightGreyBlueTwo: '#84bfda',
  coolGrey: '#9ea9b2',
  coolGreyTwo: '#a0a8b2',
  blueGrey: '#9ea2b9',
  blueGreyTwo: '#9ea1a9',
  paleGrey: '#f9fcff',
  paleGreyTwo: '#e4e4e4',
  paleGreyThree: '#fafbfd',
  darkGreyBlue: '#3a4c60',
  darkGreyBlueTwo: '#2f354f',
  whiteSmoke: '#ebebeb',
  cloudyBlue: '#c9cedb',
  cloudyBlueTwo: '#bfc1c6',
  paleSkyBlue: '#d8dfe5',
  paleSkyBlueTwo: '#d7dee4',
  turquoiseBlue: '#60d2e8',
  red: '#d60000',
  error: '#e74c3c',
  bgError: '#e74c3c1a',
  cookieBg: '#606066',
  linkedIn: '#0076b0',
  textSecondary: '#4d535a',
  // New Sainoo platform colors
  navy: '#0D1B2A',
  navyMid: '#1A2B3C',
  navyLt: '#1E3A5F',
  green: '#2DBD96',
  greenDk: '#1A9B78',
  gold: '#C9A84C',
  offWhite: '#F4F6F9',
  lightGray: '#E8ECF0',
  midGray: '#8A99A8',
  darkText: '#1A2332'
};

const sizes = {
  xs: 28,
  sm: 32,
  md: 40,
  lg: 52,
  xl: 60,
  xxl: 100,
  input: 46,
  button: 52,
  landingHeader: 74,
  appHeader: 60,
  footer: 70
};
//remove after migration to v5
const heights = sizes;
const minHeights = {
  langingContent: `calc(100vh - ${sizes.landingHeader}px)`
};

const shadows = {
  shadow1: '0 1px 3px 0 rgba(77, 83, 90, 0.15)',
  shadow2: '0 2px 6px 0 rgba(77, 83, 90, 0.1)',
  card: '0 2px 40px 0 rgba(12, 16, 20, 0.12)',
  image: '0 2px 40px 0 rgba(12, 16, 20, 0.2)'
};

const buttons = {
  bPrimary: css`
    padding: 0 15px;
    width: 100%;
    color: ${colors.white};
    background-color: ${colors.greenBlue};
    text-transform: uppercase;
    &:hover {
      background-color: ${colors.hoverButton};
    }
    &:disabled {
      color: ${colors.white};
      background-color: ${colors.iceBlue};
    }
  `,
  bSecondary: {
    padding: '0 15px',
    width: '100%',
    color: colors.secondary,
    backgroundColor: colors.white,
    border: `2px solid ${colors.secondary}`,
    lineHeight: '48px',
    '&:hover': {
      color: colors.secondaryHover,
      borderColor: colors.secondaryHover
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      color: colors.white
    }
  },
  tertiary: {
    padding: '0 15px',
    width: '100%',
    color: colors.topaz,
    backgroundColor: colors.white,
    lineHeight: '52px',
    '&:hover': {
      color: rgba(colors.topaz, 0.9),
      backgroundColor: rgba(colors.white, 0.9)
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      pointerEvents: 'none'
    }
  },
  ca: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.weirdGreen}`,
    borderRadius: '2px',
    color: colors.weirdGreen,
    transition: 'background 0.2s cubic-bezier(0.4, 0, 1, 1)',
    '&:hover': {
      color: colors.white,
      background: colors.weirdGreen
    },
    '&:active': {
      color: colors.white,
      background: colors.weirdGreen
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      borderColor: colors.iceBlue,
      color: colors.white
    }
  },
  caInverted: {
    transition: 'background 0.2s cubic-bezier(0.4, 0, 1, 1)',
    padding: '0 20px',
    backgroundColor: colors.weirdGreen,
    border: `1px solid ${colors.weirdGreen}`,
    borderRadius: '2px',
    color: colors.white,
    '&:hover': {
      borderColor: colors.weirdGreenDarken,
      backgroundColor: colors.weirdGreenDarken
    },
    '&:active': {
      borderColor: colors.weirdGreenDarken,
      backgroundColor: colors.weirdGreenDarken
    },
    '&:disabled': {
      backgroundColor: colors.iceBlue,
      borderColor: colors.iceBlue,
      color: colors.white
    }
  },
  linkedIn: {
    backgroundColor: colors.linkedIn,
    border: `1px solid ${colors.linkedIn}`,
    color: colors.white,
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.linkedIn
    }
  }
};

const inputs = {
  primary: {
    width: '100%',
    height: '46px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.iceBlue,
    borderRadius: '1px',
    backgroundColor: colors.white,
    color: colors.text,
    fontSize: '16px'
  },
  secondary: {
    padding: '0px 15px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.paleSkyBlueTwo,
    backgroundColor: 'transparent',
    borderRadius: '4px',
    color: colors.textSecondary,
    fontSize: '14px',
    '&::placeholder': {
      color: '#a0a8b2'
    },
    '&:disabled': {
      color: '#cacfd4',
      '&::placeholder': {
        color: '#cacfd4'
      }
    },
    '&:focus': {
      '&::placeholder': {
        color: '#a0a8b2'
      }
    }
  },
  ca: {
    padding: '0px 15px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.iceBlueTwo,
    borderRadius: '2px',
    color: colors.textSecondary,
    '&::placeholder': {
      color: colors.coolGrey
    },
    '&:disabled': { backgroundColor: colors.iceBlue }
  }
};

const links = {
  primary: {
    color: colors.azure,
    '&:visited': colors.linkPrimaryVisited
  }
};

const cards = {
  primary: {
    padding: '20px',
    backgroundColor: colors.white,
    borderRadius: '4px',
    boxShadow: shadows.shadow2
  }
};

const createColorStyle = (initialColor, activeColor) => ({
  color: colors[initialColor],
  '&:hover': {
    color: colors[activeColor]
  }
});

const createButtonColorStyle = (bg, color) => css`
  &,
  &:focus {
    background-color: ${colors[bg]};
    border-color: ${colors[color]};
    color: ${colors[color]};
  }
  &:hover {
    background-color: ${colors[color]};
    color: ${colors[bg]};
  }
`;
const createInvertedButtonColorStyle = (bg, color) => css`
  &,
  &:focus {
    background-color: ${colors[color]};
    border-color: ${colors[color]};
    color: ${colors[bg]};
  }
  &:hover {
    background-color: ${colors[bg]};
    color: ${colors[color]};
  }
`;

const colorStyles = {
  primary: createColorStyle('primary', 'primaryHover'),
  secondary: createColorStyle('secondary', 'secondaryHover'),
  tertiaryToSecondary: createColorStyle('tertiary', 'secondary'),
  coolGreyTwoToSecondary: createColorStyle('coolGreyTwo', 'secondary'),
  coolGreyTwoToGunMetal: createColorStyle('coolGreyTwo', 'textSecondary'),
  paleGreyTwo: createColorStyle('paleGreyTwo', 'aquaBlue'),
  darkGreyBlueToAquaBlue: createColorStyle('darkGreyBlue', 'aquaBlue'),
  whiteToAquaBlue: createColorStyle('white', 'aquaBlue'),
  coolGreyToAquaBlue: createColorStyle('coolGrey', 'aquaBlue'),
  weirdGreen: createColorStyle('weirdGreen', 'weirdGreenDarken'),
  textSecondary: createButtonColorStyle('white', 'textSecondary'),
  secondaryInverted: createInvertedButtonColorStyle('white', 'secondary'),
  disabled: css`
    color: ${colors.white};
    background-color: ${colors.iceBlue};
  `
};

export const landingTheme = createTheme({
  breakpoints,
  zIndices,
  space,
  heights,
  minHeights,
  sizes,
  fonts,
  fontSizes,
  colors,
  colorStyles,
  shadows,
  buttons,
  inputs,
  links,
  cards
});
